import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Plus, X, ChevronLeft, ChevronRight, Camera, Image, Send, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stories() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/stories`);
      // Group stories by user/store
      const grouped = groupStoriesByUser(res.data || []);
      setStories(grouped);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupStoriesByUser = (storyItems) => {
    const groups = {};
    storyItems.forEach(item => {
      const key = item.store_id || item.user_id;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          name: item.store_name || item.user_name,
          avatar: item.avatar || item.store_name?.charAt(0) || 'S',
          items: [],
          hasUnviewed: false
        };
      }
      groups[key].items.push(item);
      if (!item.viewed) {
        groups[key].hasUnviewed = true;
      }
    });
    return Object.values(groups).sort((a, b) => {
      // Unviewed first, then by most recent
      if (a.hasUnviewed && !b.hasUnviewed) return -1;
      if (!a.hasUnviewed && b.hasUnviewed) return 1;
      const aTime = new Date(a.items[0]?.created_at || 0).getTime();
      const bTime = new Date(b.items[0]?.created_at || 0).getTime();
      return bTime - aTime;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleCreateStory = async () => {
    if (!selectedFile) {
      toast.error('Please select an image or video');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('caption', caption);
      
      await axios.post(`${API_URL}/stories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Story posted!');
      setCreateOpen(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setCaption('');
      fetchStories();
    } catch (error) {
      toast.error('Failed to post story');
    } finally {
      setUploading(false);
    }
  };

  const openStory = (index) => {
    setCurrentStoryIndex(index);
    setCurrentItemIndex(0);
    setViewerOpen(true);
    markAsViewed(stories[index].items[0]?.id);
  };

  const markAsViewed = async (storyId) => {
    if (!storyId) return;
    try {
      await axios.post(`${API_URL}/stories/${storyId}/view`);
    } catch (error) {
      // Silent fail
    }
  };

  const nextItem = () => {
    const currentStory = stories[currentStoryIndex];
    if (currentItemIndex < currentStory.items.length - 1) {
      const newIndex = currentItemIndex + 1;
      setCurrentItemIndex(newIndex);
      markAsViewed(currentStory.items[newIndex]?.id);
    } else if (currentStoryIndex < stories.length - 1) {
      const newStoryIndex = currentStoryIndex + 1;
      setCurrentStoryIndex(newStoryIndex);
      setCurrentItemIndex(0);
      markAsViewed(stories[newStoryIndex].items[0]?.id);
    } else {
      setViewerOpen(false);
    }
  };

  const prevItem = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    } else if (currentStoryIndex > 0) {
      const newStoryIndex = currentStoryIndex - 1;
      setCurrentStoryIndex(newStoryIndex);
      setCurrentItemIndex(stories[newStoryIndex].items.length - 1);
    }
  };

  const currentStory = stories[currentStoryIndex];
  const currentItem = currentStory?.items[currentItemIndex];

  return (
    <div className="mb-6" data-testid="stories-component">
      {/* Stories Row */}
      <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
        {/* Add Story Button */}
        <button
          onClick={() => setCreateOpen(true)}
          className="flex-shrink-0 flex flex-col items-center gap-1"
          data-testid="add-story-btn"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300 hover:border-orange-400 transition-colors">
            <Plus className="w-6 h-6 text-slate-400" />
          </div>
          <span className="text-xs text-slate-500 font-medium">Add Story</span>
        </button>

        {/* Story Bubbles */}
        {loading ? (
          <div className="flex gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex flex-col items-center gap-1 animate-pulse">
                <div className="w-16 h-16 rounded-full bg-slate-200" />
                <div className="w-12 h-3 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        ) : stories.length === 0 ? (
          <p className="text-sm text-slate-400 pl-4">No stories yet. Be the first to share!</p>
        ) : (
          stories.map((story, idx) => (
            <button
              key={story.id}
              onClick={() => openStory(idx)}
              className="flex-shrink-0 flex flex-col items-center gap-1"
              data-testid={`story-bubble-${idx}`}
            >
              <div className={`w-16 h-16 rounded-full p-0.5 ${story.hasUnviewed ? 'bg-gradient-to-br from-orange-500 to-amber-500' : 'bg-slate-300'}`}>
                <div className="w-full h-full rounded-full bg-white p-0.5">
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-orange-600 font-bold text-lg">
                    {story.avatar}
                  </div>
                </div>
              </div>
              <span className="text-xs text-slate-600 font-medium truncate max-w-[64px]">{story.name}</span>
            </button>
          ))
        )}
      </div>

      {/* Story Viewer Modal */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-md p-0 bg-black rounded-2xl overflow-hidden border-0">
          {currentStory && currentItem && (
            <div className="relative h-[80vh] max-h-[700px]">
              {/* Progress Bars */}
              <div className="absolute top-2 left-2 right-2 z-20 flex gap-1">
                {currentStory.items.map((_, idx) => (
                  <div 
                    key={idx} 
                    className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden"
                  >
                    <div 
                      className={`h-full bg-white transition-all duration-300 ${
                        idx < currentItemIndex ? 'w-full' : idx === currentItemIndex ? 'w-full' : 'w-0'
                      }`}
                    />
                  </div>
                ))}
              </div>

              {/* Header */}
              <div className="absolute top-6 left-4 right-4 z-20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-orange-600 font-bold">
                    {currentStory.avatar}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{currentStory.name}</p>
                    <p className="text-white/60 text-xs">
                      {formatDistanceToNow(new Date(currentItem.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setViewerOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex items-center justify-center">
                {currentItem.media_type === 'video' ? (
                  <video 
                    src={currentItem.media_url}
                    className="max-w-full max-h-full object-contain"
                    autoPlay
                    muted
                    playsInline
                    onEnded={nextItem}
                  />
                ) : (
                  <img 
                    src={currentItem.media_url}
                    alt="Story"
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>

              {/* Caption */}
              {currentItem.caption && (
                <div className="absolute bottom-16 left-4 right-4 z-20">
                  <p className="text-white text-sm bg-black/40 px-4 py-2 rounded-xl backdrop-blur-sm">
                    {currentItem.caption}
                  </p>
                </div>
              )}

              {/* Navigation Buttons */}
              <button 
                onClick={prevItem}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center z-20 hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button 
                onClick={nextItem}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center z-20 hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>

              {/* Click Areas */}
              <div className="absolute inset-0 z-10 flex">
                <div className="w-1/3 h-full" onClick={prevItem} />
                <div className="w-1/3 h-full" />
                <div className="w-1/3 h-full" onClick={nextItem} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Story Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Share a Story</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Preview */}
            {previewUrl ? (
              <div className="relative aspect-[9/16] max-h-[400px] bg-slate-100 rounded-xl overflow-hidden">
                {selectedFile?.type.startsWith('video/') ? (
                  <video src={previewUrl} className="w-full h-full object-cover" controls />
                ) : (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                )}
                <button 
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                  }}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-[9/16] max-h-[400px] bg-slate-100 rounded-xl flex flex-col items-center justify-center gap-3 hover:bg-slate-200 transition-colors border-2 border-dashed border-slate-300"
              >
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                  <Camera className="w-8 h-8 text-orange-500" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-slate-700">Add Photo or Video</p>
                  <p className="text-sm text-slate-500">Max 10MB</p>
                </div>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Caption */}
            <div>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                rows={2}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                maxLength={200}
              />
              <p className="text-xs text-slate-400 text-right mt-1">{caption.length}/200</p>
            </div>

            {/* Submit */}
            <Button 
              onClick={handleCreateStory}
              disabled={!selectedFile || uploading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-11"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Share Story
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
